/**
 * SearchUsersContent Component
 * Main content for search users sheet
 * Coordinates all sub-components and uses useSearchUsers hook
 * Matches Flutter implementation: lib/presentation/bottom_sheet/search_users_bottom_sheet.dart
 */

import React, { useCallback } from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { SearchBar } from '@/presentation/components/common/SearchBar';
import { MessageInput } from '@/presentation/components/common/MessageInput';
import { SearchUserSelectedUsersHeader } from './SearchUserSelectedUsersHeader';
import { SearchUsersUsersList } from './SearchUsersUsersList';
import { useSearchUsers } from '../hooks/useSearchUsers';
import type { ShareInfoEntity } from '@/domain/entities/info/shareInfoEntity';
import type { ExclusiveContentSubscriptionEntity } from '@/domain/entities/exclusiveContentSubscription/exclusiveContentSubscriptionEntity';

export type SearchUsersMode = 'message' | 'mention';

export interface SearchUsersContentProps {
  shareInfo?: ShareInfoEntity;
  mode?: SearchUsersMode;
  /** Only invoked in 'mention' mode — tapping a user resolves the picker. */
  onSelectUser?: (sub: ExclusiveContentSubscriptionEntity) => void;
}

export const SearchUsersContent: React.FC<SearchUsersContentProps> = ({
  shareInfo,
  mode = 'message',
  onSelectUser,
}) => {
  const { l10n } = useLocale();
  const isMention = mode === 'mention';

  const {
    subscriptions,
    searchResults,
    selectedSubscriptions,
    loading,
    isSearchPending,
    hasMore,
    hasMoreSearchResults,
    searchKeyword,
    isSharing,
    setSearchKeyword,
    toggleSubscriptionSelection,
    removeSubscription,
    loadMoreSubscriptions,
    shareWithSelected,
  } = useSearchUsers({ shareInfo });

  const displayedSubscriptions = searchKeyword ? searchResults : subscriptions;

  // Hoisted out of the JSX and memoized on purpose: `SearchUsersUsersList` keys its `renderItem`
  // `useCallback` on this prop (SearchUsersUsersList.tsx, `[onUserClick]`). As an inline arrow
  // literal here it was a new function on every render of this body — every keystroke, every
  // `loading` flip, every page append — so that dep always differed and the `useCallback` paid a
  // dep comparison to hand back a fresh `renderItem` each time. All three deps below are stable:
  // `isMention` derives from the `mode` prop, `onSelectUser` is fixed at element-creation time
  // (SearchUsersSheet.tsx:69), and `toggleSubscriptionSelection` is `useCallback`-wrapped in the
  // hook (useSearchUsers.ts) — so the chain now actually memoizes. Same shape as the precedent,
  // ContentUsersLikedContent.tsx:61-76.
  //
  // Scope note, so nobody over-claims this: `VirtualList` is a plain `forwardRef` (VirtualList.tsx:450)
  // and calls `renderItem` inline (:1542, :1572), and `SearchUsersUserItem` is not memoized either
  // — so rows still re-render with this subtree. The win is a referentially honest `renderItem`
  // and the precondition for a future `React.memo`, not a measured row-render saving today.
  const handleUserClick = useCallback(
    (sub: ExclusiveContentSubscriptionEntity) => {
      if (isMention) {
        onSelectUser?.(sub);
      } else {
        toggleSubscriptionSelection(sub);
      }
    },
    [isMention, onSelectUser, toggleSubscriptionSelection],
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <SearchBar
        value={searchKeyword}
        onChange={setSearchKeyword}
        placeholder={l10n.searchUsers.searchPlaceholder}
        qa={{ id: 'search-users-input' }}
      />

      {!isMention && (
        <SearchUserSelectedUsersHeader
          subscriptions={selectedSubscriptions}
          onRemove={removeSubscription}
          emptyMessage={l10n.searchUsers.selectedUsersEmpty}
        />
      )}

      <SearchUsersUsersList
        subscriptions={displayedSubscriptions}
        onUserClick={handleUserClick}
        // TODO: @claude add a follow up task for this: search-mode load-more gate — while a keyword is
        // active the web gates the shared onLoadMore (which always calls loadMoreSubscriptions) on
        // hasMoreSearchResults, and useSearchUsers.loadMoreSearchResults has no call site — Flutter
        // lib/presentation/bottom_sheet/search_users_bottom_sheet.dart:208-218 gates the same call on
        // _hasMoreSubscriptions and never paginates search results by scroll. The follow-up MUST split
        // the two concerns instead of just setting hasMore={hasMore}: this same prop double-duties as
        // the empty-state gate one component down (SearchUsersUsersList.tsx:71, !loading && !hasMore &&
        // subscriptions.length === 0), which is what reproduces Flutter's keyword-aware search empty
        // state at search_users_bottom_sheet.dart:703 (copy at :713) — so the load-more gate follows the
        // subscriptions hasMore (Flutter :209) while the empty-state gate keeps the keyword-aware value
        // (Flutter :703).
        onLoadMore={loadMoreSubscriptions}
        hasMore={searchKeyword ? hasMoreSearchResults : hasMore}
        loading={loading || isSearchPending}
        emptyMessage={
          searchKeyword ?
            l10n.searchUsers.noSearchResults :
            l10n.searchUsers.noSubscriptions
        }
      />

      {!isMention && (
        <MessageInput
          onSend={shareWithSelected}
          disabled={selectedSubscriptions.length === 0 || isSharing}
          loading={isSharing}
          placeholder={l10n.searchUsers.writeMessage}
          qa={{ id: 'search-users-message' }}
          sendQa={{ id: 'search-users-confirm' }}
        />
      )}
    </div>
  );
};
